import { cache } from 'react';
import { UserCheck, ShieldCheck, Home, Star, FileText, Eye, Send } from 'lucide-react';

export interface NavigationStep {
  id: string;
  label: string;
  path: string;
  icon: any;
  description?: string;
  requiredFields?: string[];
  dependencies?: string[];
}

export interface NavigationState {
  currentStep: string;
  completedSteps: string[];
  availableSteps: string[];
  canNavigateTo: (stepId: string) => boolean;
  isStepComplete: (stepId: string) => boolean;
  getNextStep: () => NavigationStep | null;
  getPreviousStep: () => NavigationStep | null;
}

export const LEAVE_REVIEW_STEPS: NavigationStep[] = [
  {
    id: 'preparation',
    label: 'Preparation',
    path: '/leave-review/preparation',
    icon: UserCheck,
    description: 'Getting started with your review',
    requiredFields: [],
    dependencies: []
  },
  {
    id: 'verification',
    label: 'Verification',
    path: '/leave-review/verification',
    icon: ShieldCheck,
    description: 'Identity and contact verification',
    requiredFields: ['firstName', 'lastName', 'email', 'phone'],
    dependencies: ['preparation']
  },
  {
    id: 'property-details',
    label: 'Property Details',
    path: '/leave-review/property-details',
    icon: Home,
    description: 'Property information and rental history',
    requiredFields: ['country', 'state', 'city', 'propertyType', 'moveInDate', 'rentPrice'],
    dependencies: ['preparation', 'verification']
  },
  {
    id: 'experience',
    label: 'Experience',
    path: '/leave-review/experience',
    icon: Star,
    description: 'Ratings and detailed review',
    requiredFields: ['summaryRatings', 'detailedReview', 'pros', 'cons'],
    dependencies: ['preparation', 'verification', 'property-details']
  },
  {
    id: 'documents',
    label: 'Documents',
    path: '/leave-review/documents',
    icon: FileText,
    description: 'Upload supporting documents and images',
    requiredFields: ['propertyImages', 'identityDocuments', 'residencyDocuments'],
    dependencies: ['preparation', 'verification', 'property-details', 'experience']
  },
  {
    id: 'review',
    label: 'Review',
    path: '/leave-review/review',
    icon: Eye,
    description: 'Review all information before submission',
    requiredFields: [],
    dependencies: ['preparation', 'verification', 'property-details', 'experience', 'documents']
  },
  {
    id: 'submit',
    label: 'Submit',
    path: '/leave-review/submit',
    icon: Send,
    description: 'Final submission and commission setup',
    requiredFields: ['termsAccepted'],
    dependencies: ['preparation', 'verification', 'property-details', 'experience', 'documents', 'review']
  }
];

export function getStepById(stepId: string): NavigationStep | undefined {
  return LEAVE_REVIEW_STEPS.find(step => step.id === stepId);
}

export function getStepByPath(path: string): NavigationStep | undefined {
  return LEAVE_REVIEW_STEPS.find(step => path.startsWith(step.path));
}

export function getCurrentStepFromPath(pathname: string): NavigationStep | null {
  for (const step of LEAVE_REVIEW_STEPS) {
    if (pathname.startsWith(step.path)) {
      return step;
    }
  }
  return null;
}

export function canNavigateToStep(
  targetStepId: string,
  completedSteps: string[] = []
): boolean {
  const targetStep = getStepById(targetStepId);
  if (!targetStep) return false;

  // Allow navigation to completed steps
  if (completedSteps.includes(targetStepId)) {
    return true;
  }

  return targetStep.dependencies?.every(dep => 
    completedSteps.includes(dep)
  ) ?? true;
}

export function getNextStep(currentStepId: string): NavigationStep | null {
  const currentIndex = LEAVE_REVIEW_STEPS.findIndex(step => step.id === currentStepId);
  if (currentIndex >= 0 && currentIndex < LEAVE_REVIEW_STEPS.length - 1) {
    return LEAVE_REVIEW_STEPS[currentIndex + 1];
  }
  return null;
}

export function getPreviousStep(currentStepId: string): NavigationStep | null {
  const currentIndex = LEAVE_REVIEW_STEPS.findIndex(step => step.id === currentStepId);
  if (currentIndex > 0) {
    return LEAVE_REVIEW_STEPS[currentIndex - 1];
  }
  return null;
}

export function getNavigationProgress(currentStepId: string): number {
  const currentIndex = LEAVE_REVIEW_STEPS.findIndex(step => step.id === currentStepId);
  if (currentIndex >= 0) {
    return ((currentIndex + 1) / LEAVE_REVIEW_STEPS.length) * 100;
  }
  return 0;
}

export function generateBreadcrumbs(currentStepId: string): Array<{
  label: string;
  href?: string;
  isActive: boolean;
}> {
  const currentStep = getStepById(currentStepId);
  
  return [
    { label: 'Dashboard', href: '/dashboard/tenant', isActive: false },
    { label: 'Leave Review', href: '/leave-review', isActive: false },
    { 
      label: currentStep?.label || 'Unknown Step',
      isActive: true
    }
  ];
}

export const createNavigationState = cache((
  currentStepId: string,
  completedSteps: string[] = []
): NavigationState => {
  return {
    currentStep: currentStepId,
    completedSteps,
    availableSteps: LEAVE_REVIEW_STEPS
      .filter(step => canNavigateToStep(step.id, completedSteps))
      .map(step => step.id),
    
    canNavigateTo: (stepId: string) => canNavigateToStep(stepId, completedSteps),
    
    isStepComplete: (stepId: string) => completedSteps.includes(stepId),
    
    getNextStep: () => getNextStep(currentStepId),
    
    getPreviousStep: () => getPreviousStep(currentStepId)
  };
});

export function validateNavigationTransition(
  fromStepId: string,
  toStepId: string,
  completedSteps: string[] = []
): { allowed: boolean; reason?: string } {
  const fromStep = getStepById(fromStepId);
  const toStep = getStepById(toStepId);

  if (!fromStep || !toStep) {
    return { allowed: false, reason: 'Invalid step ID' };
  }

  if (completedSteps.includes(toStepId)) {
    return { allowed: true };
  }

  const nextStep = getNextStep(fromStepId);
  if (nextStep && nextStep.id === toStepId) {
    return { allowed: true };
  }

  if (!canNavigateToStep(toStepId, completedSteps)) {
    return { 
      allowed: false, 
      reason: `Step '${toStepId}' requires completion of: ${
        toStep.dependencies?.filter(dep => !completedSteps.includes(dep)).join(', ') || 'previous steps'
      }` 
    };
  }

  return { allowed: true };
}

export function trackNavigationEvent(
  event: 'step_enter' | 'step_exit' | 'step_complete' | 'navigation_blocked',
  stepId: string,
  metadata?: Record<string, any>
) {
  if (typeof window !== 'undefined' && (window as any).gtag) {
    (window as any).gtag('event', 'leave_review_navigation', {
      event_category: 'Navigation',
      event_label: stepId,
      event_action: event,
      ...metadata
    });
  }
}

export function getNavigationPrefetchHints(currentStepId: string): string[] {
  const hints: string[] = [];
  
  const nextStep = getNextStep(currentStepId);
  if (nextStep) {
    hints.push(nextStep.path);
  }
  
  const prevStep = getPreviousStep(currentStepId);
  if (prevStep) {
    hints.push(prevStep.path);
  }
  
  return hints;
}

export default {
  LEAVE_REVIEW_STEPS,
  getStepById,
  getStepByPath,
  getCurrentStepFromPath,
  canNavigateToStep,
  getNextStep,
  getPreviousStep,
  getNavigationProgress,
  generateBreadcrumbs,
  createNavigationState,
  validateNavigationTransition,
  trackNavigationEvent,
  getNavigationPrefetchHints
}; 